import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useRouter } from "next/router";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { useDealContext, useMutateDealContext } from "~/context/DealContext";
import type { PartnerAssignmentFormValues } from "~/models/DealPartnerAssignment";
import {
	DECLINE_REASON_CATEGORIES,
	DECLINE_REASON_OPTIONS,
	partnerAssignmentResponseSchema
} from "~/models/DealPartnerAssignment";
import { PATH as DEALS_INDEX_PATH } from "~/pages/vendors/deals";
import { isSuccess } from "~/utils/api/api_handler";
import { paths, update } from "~/utils/api/vendors/deals/DealPartnerAssignment";

export function PartnerAssignmentResponseForm() {
	const deal = useDealContext();
	const mutateDeal = useMutateDealContext();
	const router = useRouter();
	const {
		register,
		handleSubmit,
		setValue,
		control,
		formState: { errors }
	} = useForm<PartnerAssignmentFormValues>({
		defaultValues: {
			answerType: "accept",
			declineReasonCategory: "",
			declineReasonDetail: ""
		},
		resolver: yupResolver(partnerAssignmentResponseSchema)
	});

	const answerType = useWatch({ control, name: "answerType" });
	const declineReasonCategory = useWatch({
		control,
		name: "declineReasonCategory"
	});

	const { trigger, isMutating } = useSWRMutation(
		paths.update(deal.id),
		async (_key, { arg }: { arg: PartnerAssignmentFormValues }) =>
			update(deal.id, arg)
	);

	const onSubmit = async (data: PartnerAssignmentFormValues) => {
		const result = await trigger(data);
		if (isSuccess(result)) {
			if (data.answerType === "accept") {
				await mutateDeal();
				toast.success(
					"回答を受け付けました。ありがとうございます。立会人様と日程調整が必要な案件でしたら、続けてご対応をお願いいたします。"
				);
			} else {
				await router.push(DEALS_INDEX_PATH);
				toast.success("回答を受け付けました。");
			}
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	};

	return (
		<Flex direction="column" gap={designTokens["spacing/m"]}>
			<Heading as="h3">案件を依頼されました</Heading>
			<Paragraph>
				案件と個社マニュアルの内容を確認し、対応可否を回答してください。希望日時でご都合が合わない場合、同日内での時間調整については立会人様にご相談いただいて問題ございません。
			</Paragraph>
			<form noValidate onSubmit={handleSubmit(onSubmit)}>
				<FormGroup variant="vertical">
					<FormControl chip="必須" label="対応可否">
						<Controller
							control={control}
							name="answerType"
							render={({ field }) => (
								<Flex gap={designTokens["spacing/s"]}>
									<InputRadio
										checked={field.value === "accept"}
										isInvalid={!!errors.answerType}
										label="対応可能"
										onChange={() => {
											field.onChange("accept");
											setValue("declineReasonCategory", "");
											setValue("declineReasonDetail", "");
										}}
										value="accept"
									/>
									<InputRadio
										checked={field.value === "decline"}
										isInvalid={!!errors.answerType}
										label="対応不可"
										onChange={() => {
											field.onChange("decline");
										}}
										value="decline"
									/>
								</Flex>
							)}
						/>
						<FormControlText error={errors.answerType?.message} />
					</FormControl>
					{answerType === "decline" && (
						<>
							<FormControl
								chip="必須"
								isInvalid={!!errors.declineReasonCategory}
								label="対応不可理由"
							>
								<InputSelect
									isInvalid={!!errors.declineReasonCategory}
									{...register("declineReasonCategory")}
								>
									<option value="">選択してください</option>
									{DECLINE_REASON_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</InputSelect>
								<FormControlText
									error={errors.declineReasonCategory?.message}
								/>
							</FormControl>
							<FormControl
								chip={
									declineReasonCategory === DECLINE_REASON_CATEGORIES.OTHER
										? "必須"
										: undefined
								}
								isInvalid={!!errors.declineReasonDetail}
								label="詳細理由"
							>
								<InputText
									isInvalid={!!errors.declineReasonDetail}
									placeholder="対応不可についての詳細な理由を入力してください"
									resize="autoRows"
									rows={4}
									{...register("declineReasonDetail")}
								/>
								<FormControlText error={errors.declineReasonDetail?.message} />
							</FormControl>
						</>
					)}
				</FormGroup>
				<Flex justifyContent="right" marginTop={designTokens["spacing/m"]}>
					<Button loading={isMutating} type="submit" variant="primary">
						回答を送信
					</Button>
				</Flex>
			</form>
		</Flex>
	);
}
